(() => {
  const selector = 'img[data-deferred-image]';
  const bundleSelector = `${selector}[data-deferred-policy="pdp-bundle"]`;
  const stickySelector = `${selector}[data-deferred-policy="pdp-sticky"]`;
  const managedSelector = `${bundleSelector}, ${stickySelector}`;
  const gallerySlideSelector =
    ':scope > slideshow-component[ref="slideshow"] > slideshow-container > slideshow-slides > slideshow-slide[data-gallery-media-index]';
  const zoomIntentSelector =
    '[data-drag-zoom-intent][data-gallery-media-index], .dialog-thumbnails-list__thumbnail[data-gallery-media-index]';
  let observer;
  let strictObserver;
  let pageLoaded = document.readyState === 'complete';

  function hydrateImage(image) {
    observer?.unobserve(image);
    strictObserver?.unobserve(image);
    const srcset = image.getAttribute('data-deferred-srcset');
    const src = image.getAttribute('data-deferred-src');
    if (image.matches(managedSelector)) image.loading = 'eager';
    if (srcset) image.setAttribute('srcset', srcset);
    if (src) image.setAttribute('src', src);
    image.removeAttribute('data-deferred-srcset');
    image.removeAttribute('data-deferred-src');
    image.removeAttribute('data-deferred-image');
  }

  function hydrate(scope) {
    if (scope.matches?.(selector)) hydrateImage(scope);
    scope.querySelectorAll?.(selector).forEach(hydrateImage);
  }

  function observe(scope) {
    const images = [];
    if (scope.matches?.(selector)) images.push(scope);
    scope.querySelectorAll?.(selector).forEach((image) => images.push(image));
    images.forEach((image) => {
      // Zoom-dialog images are intentionally outside the viewport-loading
      // policy. They are much larger than the gallery previews and should
      // only be requested when the customer shows zoom intent.
      if (image.closest('zoom-dialog')) return;
      if (image.matches(bundleSelector)) {
        if (pageLoaded) hydrateImage(image);
        else if (strictObserver) strictObserver.observe(image);
        return;
      }
      if (image.matches(stickySelector)) {
        if (pageLoaded) hydrateImage(image);
        return;
      }
      if (observer) observer.observe(image);
      else hydrateImage(image);
    });
  }

  function gallerySlides(gallery) {
    return Array.from(gallery.querySelectorAll(gallerySlideSelector));
  }

  function slideIndex(slide) {
    const value = Number.parseInt(slide?.dataset?.galleryMediaIndex || '', 10);
    return Number.isInteger(value) && value >= 0 ? value : -1;
  }

  function selectedGallerySlide(gallery, preferredSlide) {
    if (preferredSlide instanceof HTMLElement && preferredSlide.closest('media-gallery') === gallery) {
      return preferredSlide;
    }

    const slides = gallerySlides(gallery);
    const selected = slides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    if (selected) return selected;

    const slideshow = gallery.querySelector(':scope > slideshow-component[ref="slideshow"]');
    const initialIndex = Number.parseInt(slideshow?.getAttribute('initial-slide') || '0', 10) || 0;
    return slides.find((slide) => slideIndex(slide) === initialIndex) || slides[0];
  }

  function hydrateGalleryWindow(gallery, preferredSlide) {
    if (!(gallery instanceof HTMLElement)) return;

    const slides = gallerySlides(gallery);
    if (!slides.length) return;

    const selected = selectedGallerySlide(gallery, preferredSlide);
    const selectedIndex = slideIndex(selected);
    if (selectedIndex < 0) return;

    const slideshow = gallery.querySelector(':scope > slideshow-component[ref="slideshow"]');
    const infinite = slideshow?.hasAttribute('infinite') === true;
    const slideByIndex = new Map(slides.map((slide) => [slideIndex(slide), slide]));
    const lastIndex = Math.max(...slideByIndex.keys());

    for (const offset of [-1, 0, 1]) {
      let targetIndex = selectedIndex + offset;
      if (infinite) {
        if (targetIndex < 0) targetIndex = lastIndex;
        if (targetIndex > lastIndex) targetIndex = 0;
      }
      const target = slideByIndex.get(targetIndex);
      if (!target) continue;
      // Safari may keep an off-screen `loading="lazy"` slide dormant even
      // after its real src is restored. The bounded three-slide window is
      // deliberately eager so a swipe cannot reveal a placeholder.
      target.querySelectorAll(selector).forEach((image) => {
        image.loading = 'eager';
      });
      hydrate(target);
    }
  }

  function hydrateInteractiveGallery(event) {
    const gallery = event.target.closest?.('media-gallery');
    if (!gallery) return;
    const slide = event.target.closest?.('slideshow-slide[data-gallery-media-index]');
    hydrateGalleryWindow(gallery, slide);
  }

  function hydrateSelectedGalleryWindow(event) {
    const slide = event.detail?.slide;
    if (!(slide instanceof HTMLElement)) return;
    const gallery = slide.closest('media-gallery');
    if (gallery) hydrateGalleryWindow(gallery, slide);
  }

  function zoomIntent(event) {
    const intent = event.target.closest?.(zoomIntentSelector);
    if (!intent) return null;

    const index = slideIndex(intent);
    if (index < 0) return null;

    const gallery = intent.closest('media-gallery');
    if (!gallery) return null;
    return { gallery, index };
  }

  function hydrateZoomMedia(event) {
    const intent = zoomIntent(event);
    if (!intent) return;

    const zoomMedia = intent.gallery.querySelector(
      `zoom-dialog .dialog-zoomed-gallery > [data-gallery-media-index="${intent.index}"]`
    );
    if (!zoomMedia) return;

    zoomMedia.querySelectorAll(selector).forEach((image) => {
      image.loading = 'eager';
      hydrateImage(image);
    });
  }

  function hydrateZoomMediaFromKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar' && event.code !== 'Space') return;
    hydrateZoomMedia(event);
  }

  function hydrateInteractiveBundle(event) {
    const bundle = event.target.closest?.('[data-gf-bnd]');
    if (bundle) hydrate(bundle);
  }

  document.addEventListener('pointerdown', hydrateInteractiveGallery, { capture: true, passive: true });
  document.addEventListener('focusin', hydrateInteractiveGallery, { capture: true });
  document.addEventListener('slideshow:select', hydrateSelectedGalleryWindow);
  document.addEventListener('pointerover', hydrateZoomMedia, { capture: true, passive: true });
  document.addEventListener('click', hydrateZoomMedia, true);
  document.addEventListener('focusin', hydrateZoomMedia, { capture: true });
  document.addEventListener('keydown', hydrateZoomMediaFromKey, true);
  document.addEventListener('pointerover', hydrateInteractiveBundle, { capture: true, passive: true });
  document.addEventListener('pointerdown', hydrateInteractiveBundle, { capture: true, passive: true });
  document.addEventListener('focusin', hydrateInteractiveBundle, { capture: true });

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) hydrateImage(entry.target);
      }),
      { rootMargin: '300px 0px' }
    );
    strictObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) hydrateImage(entry.target);
      }),
      { rootMargin: '0px' }
    );
  }

  window.GearfinchDeferredImages = Object.freeze({ hydrate, hydrateImage });

  const hydrateLoadFallback = () => {
    pageLoaded = true;
    document.querySelectorAll(managedSelector).forEach(hydrateImage);
    // Prime only the current and adjacent carousel slides during idle time.
    // This keeps the iOS swipe path stable without downloading every gallery
    // preview and every hidden zoom original after window.load.
    const hydrateGalleries = () =>
      document.querySelectorAll('media-gallery').forEach((gallery) => hydrateGalleryWindow(gallery));
    if ('requestIdleCallback' in window) requestIdleCallback(hydrateGalleries, { timeout: 2500 });
    else setTimeout(hydrateGalleries, 1200);
  };
  if (pageLoaded) queueMicrotask(hydrateLoadFallback);
  else window.addEventListener('load', hydrateLoadFallback, { once: true });

  observe(document);
  new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      observe(node);
      if (!pageLoaded) return;
      if (node.matches?.('media-gallery')) hydrateGalleryWindow(node);
      node.querySelectorAll?.('media-gallery').forEach((gallery) => hydrateGalleryWindow(gallery));
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
